import { useCallback, useEffect, useMemo, createContext, useState, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import type { DragEndEvent } from '@dnd-kit/core';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { GuidService } from 'ui-components/services/guid-service';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import type { AutomationRule, OrderRuleAction, RuleActionType } from './types/automation-rule-types';
import { ACTION_TYPE_SET_SERVICE_COLLECTION, ACTION_TYPE_UNDEFINED } from './constants/action-types';
import { loadingStates, modalStates } from './constants/modal-states-constants';
import messages from './messages';
import { generateNewAutomationRule } from './services';
import { ACCESS_LEVELS, useRequiresRateShopperAccessLevel } from '../../../hooks/use-has-rate-shopper-access-level';
import { useAutomationRulesSetupCompleteSellerSetting } from '../../../hooks/use-seller-setting';
import { SellerSettings } from '../../../enums/seller-settings/seller-settings';
import { LogService } from '../../../services/log-service';
import { saveNewSellerSettings } from '../../../store/seller/seller.actions';
import { AutomationRulesSteps, useTrackAutomationRulesWorkflow, buildCompleteMetadata } from './hooks/use-track-automation-rules-workflow';
import { updateSellerSettingsBulk } from '../api/save-settings';
import { fetchAutomationRules, reorderAutomationRules, deleteRule, getRuleActionTypes, addRule, updateRule } from '../api/automation-rules';
import { useSystemAutomation } from '../../../contexts/system-automation-context';

type RuleSaveError = {
  response?: {
    data?: {
      errors?: Record<string, string>;
    };
  };
};

export type AutomationRulesContextValue = {
  loadingState: string;
  automationRules: AutomationRule[];
  modalState: string;
  setModalState: (state: string) => void;
  selectedRule: AutomationRule;
  setSelectedRule: (rule: AutomationRule | null | undefined) => void;
  onDragEnd: (event: DragEndEvent) => void;
  openDeleteModal: (automationRuleId: string) => void;
  deleteRule: (ruleId: string) => Promise<void>;
  openReprocessModal: () => void;
  closeAnyModal: () => void;
  ruleActionTypes: RuleActionType[];
  saveAutomationRule: (filterId: string | undefined, onSuccess?: () => void) => void;
  ruleName: string | undefined;
  setRuleName: (name: string | undefined) => void;
  setActive: () => void;
  toggleRuleActive: (rule: AutomationRule, newActive: boolean) => Promise<void>;
  formErrorOccurred: boolean | undefined;
  isDrawerLoading: boolean;
  setIsDrawerLoading: (isLoading: boolean) => void;
  isDrawerOpen: boolean;
  setIsDrawerOpen: (isOpen: boolean) => void;
  orderRuleActionIdsWithError: (string | number)[];
  shouldShowInactive: boolean;
  toggleShouldShowInactive: () => void;
  hasAccess: boolean;
  setHasAccess: (hasAccess: boolean) => void;
  ruleTypesHashMap: Record<string, boolean> | undefined;
  defaultNewAction: OrderRuleAction;
  showFeatureGatedErrorBanner: boolean;
  refreshAutomationRules: () => void;
};

const AutomationRulesContext = createContext<AutomationRulesContextValue>({} as AutomationRulesContextValue);

const useOrderRuleActionTypesFilter = () => {
  const showRateShopper = useRequiresRateShopperAccessLevel(ACCESS_LEVELS.FREEMIUM);

  return useMemo(
    () => (orderRuleActionType: RuleActionType) => {
      return orderRuleActionType.actionTypeId !== ACTION_TYPE_SET_SERVICE_COLLECTION || showRateShopper;
    },
    [showRateShopper],
  );
};

export const doesUserHaveAccessToEveryActiveRuleAction = (
  ruleActions: OrderRuleAction[] | null | undefined,
  accessTypeMap: Record<string, boolean> | null | undefined,
): boolean => {
  if (!ruleActions || !accessTypeMap) {
    return true;
  }
  return ruleActions.every((rule) => {
    const { actionTypeId = undefined, isDeleted = true } = rule || {};
    const isActionNew = !actionTypeId;
    const isActionDeleted = isDeleted;
    const isActionAccessible = actionTypeId !== undefined && accessTypeMap[actionTypeId];
    return isActionNew || isActionDeleted || isActionAccessible;
  });
};

type AutomationRulesProviderProps = {
  children: React.ReactNode;
  onRuleChange?: (rules: AutomationRule[]) => void;
  onRuleSaved?: (rule: AutomationRule) => void;
};

const AutomationRulesProvider = ({ children, onRuleChange, onRuleSaved }: AutomationRulesProviderProps) => {
  const { refreshAutomationRules: refreshRootAutomationRules } = useSystemAutomation();
  const dispatch = useDispatch();
  const isAutomationRulesSetupComplete = useAutomationRulesSetupCompleteSellerSetting();
  const [loadingState, setLoadingState] = useAsyncSafeState(loadingStates.loading);
  const [automationRules, setAutomationRules] = useAsyncSafeState([]);
  const [modalState, setModalState] = useAsyncSafeState(modalStates.closed);
  const [selectedRule, setSelectedRule] = useAsyncSafeState();
  const [shouldLoadRuleTypes, setShouldLoadRuleTypes] = useAsyncSafeState(true);
  const [ruleActionTypes, setRuleActionTypes] = useAsyncSafeState([]);
  const [ruleName, setRuleName] = useState<string | undefined>();
  const [formErrorOccurred, setFormErrorOccurred] = useState<boolean | undefined>();
  const [shouldFetchRules, setShouldFetchRules] = useAsyncSafeState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDrawerLoading, setIsDrawerLoading] = useAsyncSafeState(false);
  const [orderRuleActionIdsWithError, setOrderRuleActionIdsWithError] = useState<(string | number)[]>([]);
  const [shouldShowInactive, setShouldShowInactive] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  const [ruleTypesHashMap, setRuleTypesHashMap] = useAsyncSafeState(undefined);
  const [showFeatureGatedErrorBanner, setShowFeatureGatedErrorBanner] = useAsyncSafeState(false);

  const sortedRules = useMemo(() => [...automationRules].sort((ruleA, ruleB) => ruleA.order - ruleB.order), [automationRules]);
  const filteredRules = useMemo(
    () => sortedRules.filter((rule) => (shouldShowInactive ? true : rule.active)),
    [sortedRules, shouldShowInactive],
  );
  const trackComplete = useTrackAutomationRulesWorkflow(AutomationRulesSteps.COMPLETE);
  const trackError = useTrackAutomationRulesWorkflow(AutomationRulesSteps.ERROR);
  const defaultNewAction: OrderRuleAction = {
    actionTypeId: ACTION_TYPE_UNDEFINED,
    orderRuleActionId: GuidService.newGuid(),
  };

  const onSetAutomationRules = useCallback(
    (rules: AutomationRule[]) => {
      setAutomationRules(rules);
      if (onRuleChange) {
        onRuleChange(rules);
      }
    },
    [setAutomationRules, onRuleChange],
  );

  // clear selection when delete/reprocess modal closes
  useEffect(() => {
    if (modalState === modalStates.closed) {
      setSelectedRule(undefined);
    }
  }, [modalState, setSelectedRule]);

  const hasCompletedInitialFetchRef = useRef(false);
  // load rules
  useEffect(() => {
    if (shouldFetchRules) {
      const isRefetch = hasCompletedInitialFetchRef.current;
      setLoadingState(loadingStates.loading);
      fetchAutomationRules()
        .then((result) => {
          hasCompletedInitialFetchRef.current = true;
          onSetAutomationRules(result);
          setLoadingState(loadingStates.success);
          setShouldFetchRules(false);
          if (isRefetch) {
            refreshRootAutomationRules().catch(() => {
              // ignore errors here, they are caught within the callback and re-thrown
            });
          }
        })
        .catch(() => {
          NotificationService.showNotification({
            title: '',
            notificationType: NOTIFICATION_TYPE_ERROR,
            content: <FormattedMessage {...messages.failedToFetch} />,
            duration: NOTIFICATION_DURATIONS.MEDIUM,
          });
          setLoadingState(loadingStates.failed);
          setShouldFetchRules(false);
        });
    }
  }, [onSetAutomationRules, setLoadingState, shouldFetchRules, setShouldFetchRules, refreshRootAutomationRules]);

  const actionTypeFilter = useOrderRuleActionTypesFilter();
  useEffect(() => {
    if (shouldLoadRuleTypes) {
      getRuleActionTypes()
        .then((result) => {
          setRuleActionTypes(result);
          const accessTypeMap = result.reduce((acc: Record<string, boolean>, item: RuleActionType) => {
            return {
              ...acc,
              [item.actionTypeId]: item.hasAccess,
            };
          }, {});
          // Determine if user has access to all action types
          setHasAccess(result.every((actionType: RuleActionType) => actionType.hasAccess));
          setRuleTypesHashMap(accessTypeMap);
          setShouldLoadRuleTypes(false);
        })
        .catch(() => {
          NotificationService.showNotification({
            title: '',
            notificationType: NOTIFICATION_TYPE_ERROR,
            content: <FormattedMessage {...messages.failedToFetch} />,
            duration: NOTIFICATION_DURATIONS.MEDIUM,
          });
          setShouldLoadRuleTypes(false);
        });
    }
  }, [shouldLoadRuleTypes, setShouldLoadRuleTypes, setRuleActionTypes, setRuleTypesHashMap]);

  useEffect(() => {
    if (ruleTypesHashMap && automationRules) {
      const hasAccessToAllActions = automationRules.every((rule: AutomationRule) => {
        const ruleActions = rule?.orderRuleActions || [];
        return doesUserHaveAccessToEveryActiveRuleAction(ruleActions, ruleTypesHashMap);
      });
      setShowFeatureGatedErrorBanner(!hasAccessToAllActions);
      if (!hasAccessToAllActions) {
        setShouldShowInactive(true);
      }
    }
  }, [automationRules, ruleTypesHashMap, setShowFeatureGatedErrorBanner, setShouldShowInactive]);

  const filteredActionTypes = useMemo(() => ruleActionTypes.filter(actionTypeFilter), [actionTypeFilter, ruleActionTypes]);
  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      const savedAutomationRules = [...sortedRules];

      if (!over || active.id === over.id) {
        return;
      }

      // original index in sorted list
      const originalIndex = sortedRules.findIndex((ar) => ar.automationRuleId === active.id);
      // get the rule being dropped onto from the filtered list
      const targetRule = filteredRules.find((ar) => ar.automationRuleId === over.id);
      // get its index in the sorted list
      const targetIndex = sortedRules.findIndex((ar) => ar.automationRuleId === targetRule.automationRuleId);

      const newRules = [...sortedRules];
      newRules.splice(originalIndex, 1);

      const foundRule = sortedRules.find((rule) => rule.automationRuleId === active.id);

      if (!foundRule) {
        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.failedToReorder} />,
          duration: NOTIFICATION_DURATIONS.MEDIUM,
        });
        return;
      }
      const newRule = {
        ...foundRule,
      };
      newRules.splice(targetIndex, 0, newRule);

      const finalRules = newRules.map((item, index) => {
        return {
          ...item,
          order: index + 1,
        };
      });

      // pre-emptively set, presuming success
      onSetAutomationRules(finalRules);

      try {
        reorderAutomationRules(finalRules);
      } catch (error) {
        onSetAutomationRules(savedAutomationRules);
        LogService.logError(new Error('Failed to reorder automation rules', { cause: error }));
        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.failedToReorder} />,
          duration: NOTIFICATION_DURATIONS.MEDIUM,
        });
      }
    },
    [sortedRules, filteredRules, onSetAutomationRules],
  );

  const deleteRuleInternal = useCallback(
    async (ruleId: string) => {
      try {
        await deleteRule(ruleId);

        const newRules = [...automationRules];
        const targetIndex = newRules.findIndex((ar) => ar.automationRuleId === ruleId);
        newRules.splice(targetIndex, 1);
        onSetAutomationRules(newRules);
      } catch (error) {
        LogService.logError(new Error('Failed to delete automation rule', { cause: error }));
        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.failedToDelete} />,
          duration: NOTIFICATION_DURATIONS.MEDIUM,
        });
      }
      setModalState(modalStates.closed);
    },
    [automationRules, onSetAutomationRules, setModalState],
  );

  const setActive = useCallback(() => {
    setSelectedRule((prev: AutomationRule | undefined) => ({ ...prev, active: !prev?.active }));
  }, [setSelectedRule]);

  const toggleRuleActive = useCallback(
    async (rule: AutomationRule, newActive: boolean) => {
      const savedAutomationRules = [...automationRules];

      // Optimistically update UI
      const updatedRule = { ...rule, active: newActive };
      onSetAutomationRules(automationRules.map((r: AutomationRule) => (r.automationRuleId === rule.automationRuleId ? updatedRule : r)));

      try {
        await updateRule(updatedRule);
      } catch (error) {
        // Revert to previous state on error
        onSetAutomationRules(savedAutomationRules);
        LogService.logError(new Error('Failed to toggle automation rule active state', { cause: error }));
        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.failedToUpdate} />,
          duration: NOTIFICATION_DURATIONS.MEDIUM,
        });
      }
    },
    [automationRules, onSetAutomationRules],
  );

  const setRuleMeta = useCallback(
    (rule: AutomationRule | null | undefined) => {
      const ruleActions = rule?.orderRuleActions || [];

      setHasAccess(doesUserHaveAccessToEveryActiveRuleAction(ruleActions, ruleTypesHashMap));
      // Ensure filterId is a string to match type expectations
      const ruleWithStringFilterId = rule
        ? {
            ...rule,
            filterId: rule.filterId ? String(rule.filterId) : undefined,
          }
        : rule;
      setSelectedRule(ruleWithStringFilterId);
      setFormErrorOccurred(false);
    },
    [setSelectedRule, ruleTypesHashMap],
  );

  const openDeleteModal = useCallback(
    (automationRuleId: string) => {
      const foundRule = automationRules.find((rule: AutomationRule) => rule.automationRuleId === automationRuleId);
      if (!foundRule) {
        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.failedToFindMatchingRule} />,
          duration: NOTIFICATION_DURATIONS.MEDIUM,
        });
        return;
      }
      setRuleMeta(foundRule);
      setModalState(modalStates.openDelete);
    },
    [automationRules, setRuleMeta, setModalState],
  );

  const openReprocessModal = useCallback(() => {
    setModalState(modalStates.openReprocess);
  }, [setModalState]);

  const closeAnyModal = () => setModalState(modalStates.closed);

  const showErrorOccurred = useCallback(
    (err: RuleSaveError) => {
      const orderRuleActionErrorPrepend = 'orderRuleActions[';
      const { errors } = err?.response?.data || {};
      if (errors) {
        const { orderRuleActions } = selectedRule;
        const idsOfErroredRules = Object.keys(errors).reduce((acc: (string | number)[], key: string) => {
          if (key.indexOf(orderRuleActionErrorPrepend) >= 0) {
            const start = key.indexOf('[') + 1;
            const indexOfAction = Number.parseInt(key.substring(start, start + 1), 10);

            if (!Number.isNaN(indexOfAction)) {
              acc.push(orderRuleActions[indexOfAction]?.orderRuleActionId);
            }
          }
          return acc;
        }, []);
        setOrderRuleActionIdsWithError(idsOfErroredRules);
      }
      setFormErrorOccurred(true);
      setIsDrawerLoading(false);
    },
    [setFormErrorOccurred, selectedRule, setIsDrawerLoading],
  );

  const saveAutomationRule = useCallback(
    (filterId: string | undefined, onSuccessAction?: () => void) => {
      setOrderRuleActionIdsWithError([]);
      setIsDrawerLoading(true);
      const rule = {
        ...generateNewAutomationRule({
          selectedRule,
          filterId,
          orderRuleActions: selectedRule.orderRuleActions,
          name: ruleName,
        }),
        active: selectedRule.active,
      } as AutomationRule;
      const asyncCall = rule.automationRuleId ? updateRule : addRule;
      asyncCall(rule)
        .then(() => {
          setShouldFetchRules(true);
          setIsDrawerLoading(false);
          if (onSuccessAction) {
            onSuccessAction();
          }
          if (onRuleSaved) {
            onRuleSaved(rule);
          }
          trackComplete(buildCompleteMetadata({ rule, selectedRule, filterId }));
          if (!isAutomationRulesSetupComplete) {
            updateSellerSettingsBulk({ [SellerSettings.isAutomationRulesSetupComplete.key]: 'true' })
              .then((newSettings) => dispatch(saveNewSellerSettings(newSettings)))
              .catch((e) => LogService.logError(new Error('Failed to save automation rules setup setting', { cause: e })));
          }
        })
        .catch((err: RuleSaveError) => {
          showErrorOccurred(err);
          trackError({
            error: JSON.stringify(err?.response?.data),
            rule,
            isAiGenerated: Boolean(rule.isAiGenerated),
          });
        });
    },
    [
      selectedRule,
      ruleName,
      showErrorOccurred,
      setShouldFetchRules,
      setIsDrawerLoading,
      setOrderRuleActionIdsWithError,
      trackComplete,
      trackError,
      dispatch,
      isAutomationRulesSetupComplete,
      onRuleSaved,
    ],
  );

  const toggleShouldShowInactive = useCallback(() => {
    setShouldShowInactive((val) => !val);
  }, [setShouldShowInactive]);

  const refreshAutomationRules = useCallback(() => {
    setShouldFetchRules(true);
  }, [setShouldFetchRules]);

  const value: AutomationRulesContextValue = {
    loadingState,
    automationRules: filteredRules,
    modalState,
    setModalState,
    selectedRule,
    setSelectedRule: setRuleMeta,
    onDragEnd,
    openDeleteModal,
    deleteRule: deleteRuleInternal,
    openReprocessModal,
    closeAnyModal,
    ruleActionTypes: filteredActionTypes,
    saveAutomationRule,
    ruleName,
    setRuleName,
    setActive,
    toggleRuleActive,
    formErrorOccurred,
    isDrawerLoading,
    setIsDrawerLoading,
    isDrawerOpen,
    setIsDrawerOpen,
    orderRuleActionIdsWithError,
    shouldShowInactive,
    toggleShouldShowInactive,
    hasAccess,
    setHasAccess,
    ruleTypesHashMap,
    defaultNewAction,
    showFeatureGatedErrorBanner,
    refreshAutomationRules,
  };

  return <AutomationRulesContext.Provider value={value}>{children}</AutomationRulesContext.Provider>;
};

export { AutomationRulesContext, AutomationRulesProvider };
